import { hasValue } from '@repo/utils';

import type { TPointData } from '../../../service/entities';
import type { TTrajectoryPlotDeltas } from '../../../types/graph-trajectory';

import type { TTrajectoryOrigin } from './trajectory-origin';
import { hasSpatialOrigin, hasVerticalOrigin } from './trajectory-origin';

export type { TTrajectoryPlotDeltas, TTrajectoryExcludeReason } from '../../../types/graph-trajectory';

export function computeOffsetDeltas(
  activeOrigin: TTrajectoryOrigin,
  offsetOrigin: TTrajectoryOrigin,
): TTrajectoryPlotDeltas {
  const canHorizontal = hasSpatialOrigin(activeOrigin) && hasSpatialOrigin(offsetOrigin);
  const canVertical = hasVerticalOrigin(activeOrigin) && hasVerticalOrigin(offsetOrigin);

  const deltaN =
    canHorizontal && hasValue(activeOrigin.northing) && hasValue(offsetOrigin.northing)
      ? offsetOrigin.northing - activeOrigin.northing
      : null;
  const deltaE =
    canHorizontal && hasValue(activeOrigin.easting) && hasValue(offsetOrigin.easting)
      ? offsetOrigin.easting - activeOrigin.easting
      : null;
  const deltaTvd =
    canVertical && hasValue(activeOrigin.tvdss) && hasValue(offsetOrigin.tvdss)
      ? offsetOrigin.tvdss - activeOrigin.tvdss
      : null;

  return { deltaN, deltaE, deltaTvd };
}

/** Degrees → radians for aziVertSect. */
function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Common vertical-section coordinate relative to the active wellhead,
 * using the active trajectory aziVertSect.
 */
export function computeCommonVsec(ns: number, we: number, aziVertSectDeg: number): number {
  const azi = toRadians(aziVertSectDeg);
  return ns * Math.cos(azi) + we * Math.sin(azi);
}

export function readFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value !== null && typeof value === 'object' && 'value' in value) {
    const inner = (value as { value: unknown }).value;

    if (typeof inner === 'number' && Number.isFinite(inner)) {
      return inner;
    }
  }

  return null;
}

export type TShiftedPointFields = {
  ns: number | null;
  we: number | null;
  tvd: number | null;
  vsec: number | null;
};

/**
 * Apply overlay deltas to a reference station for charting.
 * Main trajectory passes deltas = null (no shift).
 */
export function shiftPointForOverlay(
  point: TPointData,
  deltas: TTrajectoryPlotDeltas | null,
  options: {
    applyHorizontal: boolean;
    applyVertical: boolean;
    /** When set, recompute Vsec from shifted NS/WE with this azimuth (degrees). */
    commonAziVertSectDeg?: number | null;
  },
): TShiftedPointFields {
  const nsRaw = readFiniteNumber(point.ns);
  const weRaw = readFiniteNumber(point.we);
  const tvdRaw = readFiniteNumber(point.tvd);
  const vsecRaw = readFiniteNumber(point.vsec);

  const deltaN = deltas?.deltaN ?? null;
  const deltaE = deltas?.deltaE ?? null;
  const deltaTvd = deltas?.deltaTvd ?? null;

  const canShiftHorizontal = options.applyHorizontal && hasValue(deltaN) && hasValue(deltaE);
  const ns = canShiftHorizontal && hasValue(nsRaw) ? nsRaw + deltaN : nsRaw;
  const we = canShiftHorizontal && hasValue(weRaw) ? weRaw + deltaE : weRaw;

  const canShiftVertical = options.applyVertical && hasValue(deltaTvd);
  const tvd = canShiftVertical && hasValue(tvdRaw) ? tvdRaw + deltaTvd : tvdRaw;

  const commonAzi = options.commonAziVertSectDeg;
  const vsec = (() => {
    if (typeof commonAzi === 'number' && hasValue(ns) && hasValue(we)) {
      return computeCommonVsec(ns, we, commonAzi);
    }

    return vsecRaw;
  })();

  return { ns, we, tvd, vsec };
}
